import {
  Alert,
  AlertTitle,
  Button,
  capitalize,
  CircularProgress,
  Link,
  Step,
  StepButton,
  Stepper,
} from "@mui/material";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { BasicInfoStep } from "./steps/BasicInfoStep";
import { CalibrationStep } from "./steps/CalibrationStep";
import { ConnectionsStep } from "./steps/ConnectionsStep";
import {
  editingSupported,
  tractorArchetypeToViewModel,
  TractorArchetypeViewModel,
  viewModelSchema,
  viewModelToTractorArchetype,
} from "./TractorArchetypeViewModel";
import { Form, Formik } from "formik";
import { getTractorsPath } from "portal/utils/routing";
import { Loading } from "portal/components/Loading";
import { ReviewStep } from "./steps/ReviewStep";
import { Trans, useTranslation } from "react-i18next";
import { typedPath } from "typed-path";
import { useFormikWizard } from "portal/utils/hooks/useFormikWizard";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useNavigate } from "react-router";
import { validateThen } from "portal/utils/forms";
import LockIcon from "@mui/icons-material/LockOutlined";
import React, { FC, useMemo } from "react";

interface TractorArchetypeFormProps {
  archetypeId?: string;
}

const S = typedPath<TractorArchetypeViewModel>();

export const TractorArchetypeForm: FC<TractorArchetypeFormProps> = ({
  archetypeId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeStepIndex, setActiveStepIndex, steps, StepRenderer } =
    useFormikWizard([
      {
        titleI18nKey: "views.autotractor.equipment.basicInfo",
        editedFields: [
          S.make,
          S.model,
          S.series,
          S.driveType,
          S.minTurningRadiusM,
          S.steeringRange,
          S.dimensions.wheelbaseM,
          S.dimensions.wheelSpacingM,
          S.dimensions.boundaryViewModel,
        ],
        RenderBody: BasicInfoStep,
      },
      {
        titleI18nKey: "views.autotractor.equipment.connections",
        editedFields: [S.hitches, S.frontPto, S.rearPto, S.scvOutputs],
        RenderBody: ConnectionsStep,
      },
      {
        titleI18nKey: "views.autotractor.equipment.calibration",
        editedFields: [S.calibration],
        RenderBody: CalibrationStep,
      },
      {
        titleI18nKey: "views.autotractor.equipment.review",
        editedFields: [],
        RenderBody: ReviewStep,
      },
    ]);
  const activeStep = useMemo(
    () => steps[activeStepIndex],
    [steps, activeStepIndex]
  );

  const [createTractorArchetype] = useMutationPopups(
    atkEquipmentApi.endpoints.createTractorArchetype.useMutation(),
    {
      success: capitalize(
        t("utils.actions.createdLong", {
          subject: t("models.equipment.archetype_one"),
        })
      ),
    }
  );

  const [updateTractorArchetype, { isLoading: isSaving }] = useMutationPopups(
    atkEquipmentApi.endpoints.updateTractorArchetype.useMutation(),
    {
      success: capitalize(
        t("utils.actions.updatedLong", {
          subject: t("models.equipment.archetype_one"),
        })
      ),
    }
  );
  const isEditing = Boolean(archetypeId);

  const {
    data: archetype,
    isError,
    isLoading,
  } = useQueryPopups(
    atkEquipmentApi.endpoints.getTractorArchetype.useQuery(
      { id: archetypeId || "" },
      { skip: !isEditing }
    )
  );

  if (isError) {
    navigate(getTractorsPath());
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Formik
      initialValues={tractorArchetypeToViewModel(archetype)}
      enableReinitialize
      validationSchema={viewModelSchema}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);
        if (isEditing) {
          await updateTractorArchetype(viewModelToTractorArchetype(values));
        } else {
          const res = await createTractorArchetype(
            viewModelToTractorArchetype(values)
          );
          if (!res.error) {
            navigate(getTractorsPath());
          }
        }
        setSubmitting(false);
      }}
    >
      {(formikProps) => {
        const editingUnsupportedVersion = !editingSupported(formikProps.values);

        const buttonRow = (
          <div className="flex justify-between">
            {activeStepIndex > 0 && (
              <Button
                color="inherit"
                onClick={() => setActiveStepIndex(activeStepIndex - 1)}
              >
                {t("utils.actions.backShort")}
              </Button>
            )}
            <div className="flex ml-auto gap-2">
              {activeStepIndex !== steps.length - 1 && (
                <Button
                  className="ml-auto"
                  variant="contained"
                  disabled={activeStepIndex === steps.length - 1}
                  onClick={() =>
                    validateThen(
                      formikProps,
                      () => setActiveStepIndex(activeStepIndex + 1),
                      undefined,
                      activeStep?.editedFields
                    )
                  }
                >
                  {t("utils.actions.next")}
                </Button>
              )}
              {(isEditing || activeStepIndex === steps.length - 1) && (
                <Button
                  className="ml-auto"
                  type="submit"
                  variant="contained"
                  endIcon={
                    isSaving && (
                      <CircularProgress size="small" variant="indeterminate" />
                    )
                  }
                  disabled={
                    isSaving ||
                    !formikProps.isValid ||
                    !formikProps.dirty ||
                    formikProps.isValidating ||
                    formikProps.isSubmitting
                  }
                >
                  {t("utils.actions.submit")}
                </Button>
              )}
            </div>
          </div>
        );

        return (
          <Form className="flex flex-col gap-2">
            <Stepper nonLinear={isEditing} activeStep={activeStepIndex}>
              {steps.map((s, i) => (
                <Step
                  key={`${i}-${s.titleI18nKey}`}
                  index={i}
                  completed={!isEditing && activeStepIndex > i}
                >
                  <StepButton
                    color={activeStepIndex === i ? "primary" : "inherit"}
                    onClick={() =>
                      i > activeStepIndex
                        ? validateThen(
                            formikProps,
                            () => setActiveStepIndex(i),
                            undefined,
                            activeStep?.editedFields
                          )
                        : setActiveStepIndex(i)
                    }
                  >
                    {t(s.titleI18nKey)}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
            {editingUnsupportedVersion && (
              <Alert icon={<LockIcon fontSize="inherit" />} severity="warning">
                <AlertTitle>
                  {t(
                    "views.autotractor.equipment.unsupportedVersion.tractorArchetypeLockedTitle"
                  )}
                </AlertTitle>
                <Trans
                  i18nKey="views.autotractor.equipment.unsupportedVersion.tractorArchetypeLockedBody"
                  components={[
                    <Link
                      key="0"
                      href="https://pono-maka.slack.com/archives/C07N6FW2971"
                      color="inherit"
                    />,
                  ]}
                />
              </Alert>
            )}
            {buttonRow}
            <StepRenderer disabled={editingUnsupportedVersion} />
            {buttonRow}
          </Form>
        );
      }}
    </Formik>
  );
};
